<template>
	<div>
		<div class="table_description table_description_filter padding_bottom_s">
			<div class="tertiary_color text_left">
				{{$t("marketSoundingDecision.table.description")}}
			</div>
		</div>
		<div class="table_head table_head_filter">
			<div class="flex flex_between">
				<QuickEnumFilterGroup column-name="decisionStatus"
									  :enum-def="enumDef.MarketSoundingStatus"
									  :option-values="[
										  enumDef.MarketSoundingStatus.New,
										  enumDef.MarketSoundingStatus.Interested,
										  enumDef.MarketSoundingStatus.NotInterested,
										  enumDef.MarketSoundingStatus.Expired
									  ]"
									  :default-value="enumDef.MarketSoundingStatus.New"
									  @apply-filter="applyQuickFilter"
									  storage-key="marketSoundingDecision.decisionStatus"
				/>
			</div>
		</div>
		<div class="flex filter_table">
			<div class="flex flex_column w_100">
				<AgItem :columnDefs="columnDefs"
						:rowData="rowData"
						:isRowSelectable="isRowSelectable"
						v-on:sortChanged="sortChanged"
						:expanded-groups="['paymentDetails']"
				></AgItem>
				<Pagination
						:totalNum="totalNum"
						:pageSize="pageSize"
						:sizeChange="sizeChange"
						:currentChange="currentChange"
						:currentPage="page"
				></Pagination>
			</div>
 			<MarketSoundingDecisionFilter @apply-filter="applyFilter"/>
		</div>

		<MarketSoundingDecisionConfirmModelNotInterested ref="confirmNotInterestedModal" @confirm="setNotInterested"/>
		<MarketSoundingDecisionConfirmModalInterested ref="confirmInterestedModal" @confirm="setInterested"/>
		<MarketSoundingDecisionMessageModal ref="messageModal" @messageSent="onMessageSent"/>
	</div>
</template>

<script>
import AgItem from "@/components/templates/agGrid/AgItem.vue";
import Pagination from "@/components/templates/common/Pagination.vue";
import PaginationParent from "@/components/templates/common/PaginationParent.vue";
import {enumDef} from "@/common/enumDefs";
import {marketSoundingDecisionApi} from "@/common/api/marketSoundingDecisionApi";
import MarketSoundingDecisionConfirmModelNotInterested
	from "@/components/templates/investor/riskAssessment/marketSounding/MarketSoundingDecisionConfirmModelNotInterested.vue";
import MarketSoundingDecisionConfirmModalInterested
	from "@/components/templates/investor/riskAssessment/marketSounding/MarketSoundingDecisionConfirmModalInterested.vue";
import QuickEnumFilterGroup from "@/components/newFilter/quickFilters/QuickEnumFilterGroup.vue";
import {
	asLocalizedDate,
	asLocalizedNumberWithDays,
	asMoneyAmount,
	asPercentage
} from "@/components/templates/agGrid/formatter/valueFormatters";
import ExchangeReceivablesFilter
	from "@/components/templates/investor/exchange/receivables/ExchangeReceivablesFilter.vue";
import MarketSoundingDecisionFilter
	from "@/components/templates/investor/riskAssessment/marketSounding/MarketSoundingDecisionFilter.vue";
import MarketSoundingDecisionMessageModal
	from "@/components/templates/investor/riskAssessment/marketSounding/MarketSoundingDecisionMessageModal.vue";
import {formatAsLocalizedDate, formatAsMoneyAmount, formatAsNumberWithDays, formatAsPercentage} from "@/common/format";
import {navigationUtils} from "@/common/navigation/navigationUtils";
import {navigationPaths} from "@/common/navigation/navigationPaths";
import {navigationTabs} from "@/common/navigation/navigationTabs";

export default {
		extends: PaginationParent,
		components: {
			MarketSoundingDecisionMessageModal: MarketSoundingDecisionMessageModal,
			MarketSoundingDecisionFilter,
			ExchangeReceivablesFilter,
			QuickEnumFilterGroup,
			MarketSoundingDecisionConfirmModelNotInterested,
			MarketSoundingDecisionConfirmModalInterested,
			AgItem,
			Pagination
		},
		data() {
			return {
				columnDefs: [],
				rowData: null,
				columnFilters: [],
				quickFilters: [],
				sorting: [],
				enumDef: enumDef
			};
		},
		beforeMount() {
			this.setColumnDefs();

			// Initial list is loaded when quick filters are initialized
		},
		computed:  {
			filters() {
				return [ ...this.columnFilters, ...this.quickFilters ];
			}
		},
		methods: {
			setColumnDefs() {
				this.columnDefs = [
					{
						headerName: this.$t("marketSoundingDecision.table.header.opportunity"),
						children: [
							{
								headerName: this.$t("common.status"),
								minWidth: 155,
								maxWidth: 200,
								field: "decisionStatus",
								cellRendererFramework: "EnumAsText",
								enumDef: enumDef.MarketSoundingStatus,
								sortable: true,
							},
							{
								headerName: this.$t("marketSoundingDecision.table.header.prospectiveReceivableStatus"),
								minWidth: 135,
								maxWidth: 175,
								field: "receivableState",
								cellRendererFramework: "EnumAsTextWithLinkAction",
								cellRendererParams: {
									enumDef: enumDef.MarketSoundingDecisionReceivableState,
									customization:  {
										[enumDef.MarketSoundingDecisionReceivableState.InSale]: {
											linkAction: (_) => this.$router.push(navigationUtils.getRouteWithActiveTab(navigationPaths.investor.exchange, navigationTabs.investor.exchange.receivables))
										}
									}
								}
							},
							{
								headerName: this.$t("common.seller"),
								field: "sellerName",
								minWidth: 175,
								nameType: "sellerName",
								idType: "sellerId",
								investorCompanyConfigStatusType: "investorSellerConfigStatus",
								cellRendererFramework: "CompanyTem",
								sortable: true
							},
							{
								headerName: this.$t("common.debtor"),
								field: "debtorName",
								minWidth: 175,
								nameType: "debtorName",
								idType: "debtorId",
								investorCompanyConfigStatusType: "investorDebtorConfigStatus",
								cellRendererFramework: "CompanyTem",
								sortable: true
							},
						]
					},
					{
						headerName: this.$t("marketSoundingDecision.table.header.opportunityDetails"),
						children: [
							{
								headerName: this.$t("investor.exchange.table.header.docs"),
								headerClass: "no-padding align-center",
								cellClass: "no-padding align-center",
								cellRendererFramework: "GeneralActionIconButton",
								cellRendererParams: {
									iconStyleType: "material-icons",
									iconClass: "content_paste_search",
									action: (data) => this.showReceivableDetailsModal(data)
								},
								minWidth: 60,
							},
							{
								headerName: this.$t("marketSoundingDecision.table.header.estimatedAmount"),
								headerClass: "align-right",
								field: "prospectiveReceivableAmount",
								cellClass: "align-right",
								valueFormatter: asMoneyAmount(),
								cellRendererFramework: "ValueWithComparisonSubtext",
								cellRendererParams: {
									comparisonValueGetter: (data) => formatAsMoneyAmount(data.convertedReceivableAmount),
									comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data)
								},
								minWidth: 140,
								sortable: true
							},
							{
								headerName: this.$t("common.ccy"),
								headerClass: "no-padding",
								cellClass: "no-padding",
								field: "receivableCurrency",
								minWidth: 50,
								maxWidth: 50,
								sortable: true
							},
							{
								headerName: this.$t("marketSoundingDecision.table.header.assignmentType"),
								headerComponentParams: {
									tooltip: this.$t("investor.exchange.table.header.assignmentType.tooltip"),
									showTooltipIcon: true,
								},
								field: "disclosed",
								cellRendererFramework: "EnumAsText",
								enumDef: enumDef.disclosedType,
								minWidth: 120,
								maxWidth: 130,
								sortable: true
							},
							{
								headerName: this.$t("marketSoundingDecision.table.header.invoiceNumber"),
								headerClass: "align-right",
								cellClass: "align-right",
								field: "prospectiveReceivableInvoiceNumber",
								minWidth: 160,
								cellRendererFramework: "ValueWithComparisonSubtext",
								cellRendererParams: {
									comparisonValueField: "convertedReceivableInvoiceNumber",
									comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data),
									compareFormatted: false
								},
								sortable: true,
							},
							{
								headerName: this.$t("investor.exchange.table.header.receivableType"),
								headerComponentParams: {
									tooltip: this.$t("investor.exchange.table.header.receivableType.tooltip"), // TODO: Bundle - Get bundle params from config
								},
								field: "receivableType",
								minWidth: 75,
								maxWidth: 80,
								cellRendererFramework: "EnumAsIcon",
								enumDef: enumDef.receivableType,
								sortable: true,
							},
							{
								headerName: this.$t("common.contractNumber"),
								headerClass: "align-right",
								cellClass: "align-right",
								field: "prospectiveInstallmentContractNumber",
								minWidth: 160,
								cellRendererFramework: "ValueWithComparisonSubtext",
								cellRendererParams: {
									comparisonValueField: "convertedInstallmentContractNumber",
									comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data),
									compareFormatted: false
								},
								sortable: true,
							},
						]
					},
					{
						headerName: this.$t("marketSoundingDecision.table.header.paymentDetails"),
						groupId: "paymentDetails",
						children: [
							{
								headerName: this.$t("investor.exchange.table.header.retention"),
								headerClass: "align-center",
								headerComponentParams: {
									tooltip: this.$t("investor.exchange.table.header.retention.tooltip"),
									showTooltipIcon: true,
								},
								field: "allowedRetentionRate",
								cellClass: "align-center",
								minWidth: 70,
								maxWidth: 70,
								valueFormatter: asPercentage({ showEmptyAsNotAvailable: true, decimals: 0 }),
							},
							{
								headerName: this.$t("marketSoundingDecision.table.header.estimatedPaymentTerm"),
								headerClass: "align-right",
								field: "prospectiveReceivablePaymentTerm",
								cellClass: "align-right",
								valueFormatter: asLocalizedNumberWithDays(),
								cellRendererFramework: "ValueWithComparisonSubtext",
								cellRendererParams: {
									comparisonValueGetter: (data) => formatAsNumberWithDays(data.convertedReceivablePaymentTerm),
									comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data)
								},
								minWidth: 110,
								maxWidth: 110,
								sortable: true
							},
							{
								headerName: this.$t("marketSoundingDecision.table.header.estimatedInvoiceDate"),
								headerClass: "align-right",
								field: "prospectiveReceivableInvoiceDate",
								cellClass: "align-right",
								valueFormatter: asLocalizedDate(),
								cellRendererFramework: "ValueWithComparisonSubtext",
								cellRendererParams: {
									comparisonValueGetter: (data) => formatAsLocalizedDate(data.convertedReceivableInvoiceDate),
									comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data)
								},
								minWidth: 130,
								maxWidth: 130,
								sortable: true,
								columnGroupShow: "open"
							},
							{
								headerName: this.$t("marketSoundingDecision.table.header.estimatedDueDate"),
								headerClass: "align-right",
								field: "prospectiveReceivableDueDate",
								cellClass: "align-right",
								valueFormatter: asLocalizedDate(),
								cellRendererFramework: "ValueWithComparisonSubtext",
								cellRendererParams: {
									comparisonValueGetter: (data) => formatAsLocalizedDate(data.convertedReceivableDueDate),
									comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data)
								},
								minWidth: 140,
								maxWidth: 140,
								sortable: true,
								columnGroupShow: "open"
							},
						]
					},
					{
						headerName: this.$t("investor.exchange.table.header.riskAnalysis"),
						headerGroupComponentParams: {
							tooltip: this.$t("investor.exchange.table.header.riskAnalysis.tooltip"),
						},
						children: [
							{
								headerName: this.$t("investor.exchange.table.header.riskAnalysis.dsrExistsSince"),
								headerComponentParams: {
									tooltip: this.$t("investor.exchange.table.header.riskAnalysis.dsrExistsSince.tooltip"),
									showTooltipIcon: true,
								},
								field: "dsrExistsSince",
								valueFormatter: asLocalizedDate( {showEmptyAsNotAvailable: true }),
								minWidth: 110,
								maxWidth: 110,
								sortable: true
							},
							{
								headerName: this.$t("investor.exchange.table.header.riskAnalysis.all"),
								headerComponentParams: {
									tooltip: this.$t("investor.exchange.table.header.riskAnalysis.all.tooltip"),
									showTooltipIcon: true
								},
								headerClass: "align-right",
								field: "dsrAssessmentTotalCases",
								cellClass: "align-right",
								minWidth: 130,
								maxWidth: 130,
								sortable: true
							},
							{
								headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmount"),
								headerComponentParams: {
									tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmount.tooltip"),
									showTooltipIcon: true
								},
								headerClass: "align-right",
								field: "dsrAssessmentSoldAmount",
								cellClass: "align-right",
								valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true, currencyField: "receivableCurrency" }),
								minWidth: 125,
								maxWidth: 125,
								sortable: true,
								columnGroupShow: "open"
							},
							{
								headerName: this.$t("investor.exchange.table.header.riskAnalysis.fullyPaid"),
								headerComponentParams: {
									tooltip: this.$t("investor.exchange.table.header.riskAnalysis.fullyPaid.tooltip"),
									showTooltipIcon: true
								},
								headerClass: "align-right",
								field: "dsrAssessmentFullyPaidCases",
								cellClass: "align-right",
								minWidth: 140,
								maxWidth: 140,
								sortable: true,
								columnGroupShow: "open"
							}
						]
					},
					{
						headerName: this.$t("common.evaluation"),
						headerClass: "bg_special",
						children: [
							{
								headerName: this.$t("marketSoundingDecision.table.header.estimatedIrr"),
								headerClass: "align-right",
								headerComponentParams: {
									tooltip: this.$t("investor.exchange.table.header.currentRoi.tooltip"),
									showTooltipIcon: true,
								},
								field: "prospectiveInvestorTargetIrrRatePerAnnumCapped",
								cellClass: "align-right",
								minWidth: 110,
								maxWidth: 110,
								valueFormatter: asPercentage({ showEmptyAsNotAvailable: true }),
								cellRendererFramework: "ValueWithComparisonSubtext",
								cellRendererParams: {
									comparisonValueGetter: (data) => formatAsPercentage(data.convertedInvestorTargetIrrRatePerAnnumCapped, { showEmptyAsNotAvailable: true }),
									comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data)
								},
								sortable: true,
								pinned: "right",
							},
							{
								headerName: this.$t("marketSoundingDecision.table.header.endsIn"),
								headerClass: "align-right",
								field: "opportunityEndTime",
								cellClass: "align-right",
								minWidth: 100,
								maxWidth: 125,
								cellRendererFramework: "TimeLeftCountdown",
								sortable: true,
								pinned: "right",
							},
							{
								headerName: this.$t("common.actions"),
								headerComponentFramework: "MarketSoundingDecisionTableOperationHeader",
								headerComponentParams: {
									setInterestedAllSelected: this.confirmSetInterestedSelected,
									setNotInterestedAllSelected: this.confirmSetNotInterestedSelected
								},
								cellRendererFramework: "MarketSoundingDecisionTableOperation",
								cellRendererParams: {
									setInterested: this.confirmSetInterested,
									setNotInterested: this.confirmSetNotInterested,
								},
								minWidth: 230,
								pinned: "right",
							},
							{
								headerName: this.$t("marketSoundingDecision.table.header.messageToWalbing"),
								cellClass: "align-center",
								cellRendererFramework: "MarketSoundingDecisionTableMessage",
								cellRendererParams: {
									showMessageModalFunc: this.openMessageModal,
								},
								minWidth: 95,
								pinned: "right",
							},
							{
								minWidth: 45,
								maxWidth: 45,
								headerCheckboxSelection: true,
								checkboxSelection: true,
								headerCheckboxSelectionFilteredOnly: true,
								pinned: "right",
							},
						]
					}
				];
			},
			sortChanged(sortData) {
				this.sorting = sortData;
				this.initPage();
				this.getList();
			},
			getList() {
				let postData = {
					columns: [],
					filters: this.filters || [],
					maxResults: this.maxResults,
					offset: this.offset,
					sorting: this.sorting
				};

				marketSoundingDecisionApi.inspection.list(postData)
					.then(res => {
						this.setTotalNum(res.recordsTotal);
						this.rowData = res.data.map(data => {
							return {
								...data,
								allowSetInterestedGetter: () => this.doesOpportunityAllowSetInterested(data),
								allowSetNotInterestedGetter: () => this.doesOpportunityAllowSetNotInterested(data),
							};
						});
					})
					.catch(this.toast);
			},
			doesOpportunityAllowSetInterested(data) {
				return [enumDef.MarketSoundingStatus.New, enumDef.MarketSoundingStatus.NotInterested].includes(data.decisionStatus)
					&& !this.isOpportunityTimeExpired(data);
			},
			doesOpportunityAllowSetNotInterested(data) {
				return [enumDef.MarketSoundingStatus.New, enumDef.MarketSoundingStatus.Interested].includes(data.decisionStatus)
					&& !this.isOpportunityTimeExpired(data);
			},
			isOpportunityTimeExpired(data) {
				return moment(data.opportunityEndTime).isSameOrBefore(moment());
			},
			isRowSelectable(rowNode) {
				return rowNode.data.allowSetInterestedGetter() || rowNode.data.allowSetNotInterestedGetter();
			},
			openMessageModal(data) {
				this.$refs.messageModal.showMessageModal(data);
			},
			confirmSetInterested(data) {
				this.$refs.confirmInterestedModal.showWithSingleRow(data);
			},
			confirmSetNotInterested(data) {
				this.$refs.confirmNotInterestedModal.showWithSingleRow(data);
			},
			confirmSetInterestedSelected(validSelectedRows) {
				if (validSelectedRows.length === 0) {
					this.toast(this.$t("marketSoundingDecision.selected.actionNotSupported"));
					return;
				}

				if (validSelectedRows.length === 1) {
					this.$refs.confirmInterestedModal.showWithSingleRow(validSelectedRows[0]);
				} else {
					this.$refs.confirmInterestedModal.showWithMultipleRows(validSelectedRows);
				}
			},
			confirmSetNotInterestedSelected(validSelectedRows) {
				if (validSelectedRows.length === 0) {
					this.toast(this.$t("marketSoundingDecision.selected.actionNotSupported"));
					return;
				}

				if (validSelectedRows.length === 1) {
					this.$refs.confirmNotInterestedModal.showWithSingleRow(validSelectedRows[0]);
				} else {
					this.$refs.confirmNotInterestedModal.showWithMultipleRows(validSelectedRows);
				}
			},
			setInterested(data, _, evaluation) {
				const decisionIds = data.map(row => row.decisionId);

				marketSoundingDecisionApi.operation.setInterested(decisionIds, evaluation)
					.then(() => {
						this.toastSuccess();
						this.getList();
					})
					.catch(this.toast);
			},
			setNotInterested(data, reason, evaluation) {
				const decisionIds = data.map(row => row.decisionId);

				marketSoundingDecisionApi.operation.setNotInterested(decisionIds, reason, evaluation)
					.then(() => {
						this.toastSuccess();
						this.getList();
					})
					.catch(this.toast);
			},
			applyFilter(filters) {
				this.columnFilters = filters;
			},
			applyQuickFilter(filters) {
				this.quickFilters = filters;
			},
			getConvertedValueComparisonSubtextKey(data) {
				if (data.receivableState === enumDef.MarketSoundingDecisionReceivableState.InSale) {
					return "marketSounding.valueComparison.subtext.state.selling";
				} else {
					return null;
				}
			},
			showReceivableDetailsModal(data) {
				this.$store.dispatch("setActionNoticModal", {
					show: true,
					data: {
						title: this.$t("ReceDetail.Receivable.Details"),
						type: "ReceivableDetailsModal",
						width: 800,
						data: data
					}
				});
			},
			onMessageSent() {
				this.getList();
			}
		},
		watch: {
			filters() {
				this.getList();
			}
		},
	};
</script>
